import React, { useEffect, useRef, useState } from "react";
import { Upload, Music, Trash2, ArrowUp, ArrowDown, Megaphone, Pencil, X, Mic, Volume2 } from "lucide-react";
import { api } from "../../lib/api";
import { D, JAKARTA, inputStyle, Card, SectionTitle, Btn, Chip, Field, Toggle, Msg, fmtSize } from "./ui";
import { PengaturanNarator } from "./NaratorSettings";

// Panel Radio: playlist MP3 (unggah berpotongan → object storage) + naskah pengumuman suara AI
export function RadioPanel({ token }) {
  const [data, setData] = useState({ tracks: [], announcements: [] });
  const [msg, setMsg] = useState("");
  const load = () => api.adminRadio(token).then(setData).catch(e => setMsg(e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div style={{ padding: "22px 20px 30px", fontFamily: JAKARTA }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 27, fontWeight: 800, letterSpacing: -0.6, color: D.ink }}>Radio Jawsel</h2>
        <Chip tone="blue">{data.tracks.filter(t => t.active).length} lagu aktif</Chip>
        <Chip tone="gray">{data.announcements.filter(a => a.active).length} pengumuman aktif</Chip>
        <Msg text={msg} testid="radio-msg" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))", gap: 18, alignItems: "start" }}>
        <Playlist token={token} tracks={data.tracks} reload={load} setMsg={setMsg} />
        <Announcements token={token} items={data.announcements} reload={load} setMsg={setMsg} />
      </div>
    </div>
  );
}

function Playlist({ token, tracks, reload, setMsg }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: "", artist: "" });
  const [progress, setProgress] = useState(null);
  const [preview, setPreview] = useState(null);

  function pickFile(f) {
    if (!f) return;
    setFile(f);
    if (!form.title) setForm(s => ({ ...s, title: f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ") }));
  }

  async function upload(e) {
    e.preventDefault();
    if (!file) return setMsg("Pilih file audio dulu");
    setMsg("");
    try {
      setProgress(0);
      const m = await api.uploadMedia(token, file, "audio", setProgress);
      await api.addTrack(token, { media_id: m.id, title: form.title || file.name, artist: form.artist });
      setFile(null); setForm({ title: "", artist: "" }); if (fileRef.current) fileRef.current.value = "";
      setMsg("Lagu ditambahkan");
      reload();
    } catch (err) { setMsg(err.message); }
    finally { setProgress(null); }
  }

  async function move(idx, dir) {
    const ids = tracks.map(t => t.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    await api.reorderTracks(token, ids).catch(e => setMsg(e.message));
    reload();
  }
  async function del(t) {
    if (!window.confirm(`Hapus "${t.title}" dari playlist?`)) return;
    await api.deleteTrack(token, t.id).catch(e => setMsg(e.message));
    if (preview === t.id) setPreview(null);
    reload();
  }

  return (
    <Card testid="radio-playlist">
      <SectionTitle title="Playlist" sub="Lagu diputar berurutan di radio kota. Format MP3/AAC/OGG/WAV, maks 15 MB." />
      <form onSubmit={upload} data-testid="track-form" style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: D.cream, borderRadius: 18, marginBottom: 16 }}>
        <div onClick={() => fileRef.current?.click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
          data-testid="track-dropzone"
          style={{ border: `2px dashed ${file ? D.blue : D.gray}`, borderRadius: 14, padding: "16px 14px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", background: "#fff" }}>
          <span style={{ width: 40, height: 40, borderRadius: 12, background: D.blueSoft, display: "grid", placeItems: "center", flex: "none" }}><Upload size={18} color={D.blue} /></span>
          <div style={{ minWidth: 0 }}>
            <b style={{ fontSize: 13, color: D.ink, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file ? file.name : "Pilih atau seret file audio ke sini"}</b>
            <span style={{ fontSize: 12, color: D.sub }}>{file ? fmtSize(file.size) : "Klik untuk memilih dari perangkat"}</span>
          </div>
          <input ref={fileRef} data-testid="track-file" type="file" accept="audio/*" style={{ display: "none" }} onChange={e => pickFile(e.target.files?.[0])} />
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Field label="Judul" grow><input data-testid="track-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Judul lagu" style={inputStyle} required /></Field>
          <Field label="Artis" grow><input data-testid="track-artist" value={form.artist} onChange={e => setForm({ ...form, artist: e.target.value })} placeholder="Nama artis (opsional)" style={inputStyle} /></Field>
        </div>
        {progress != null && (
          <div data-testid="track-progress" style={{ height: 8, borderRadius: 99, background: "#fff", overflow: "hidden" }}>
            <div style={{ width: `${Math.round(progress * 100)}%`, height: "100%", background: D.blue, transition: "width .2s" }} />
          </div>
        )}
        <div><Btn type="submit" testid="track-submit" disabled={progress != null}><Music size={15} /> {progress != null ? `Mengunggah ${Math.round(progress * 100)}%` : "Tambah ke Playlist"}</Btn></div>
      </form>

      <ol data-testid="track-list" style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        {tracks.map((t, i) => (
          <li key={t.id} data-testid={`track-${t.id}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 16, border: `1.5px solid ${D.line}`, opacity: t.active ? 1 : .55 }}>
            <span style={{ width: 28, textAlign: "center", fontSize: 12, fontWeight: 800, color: D.sub }}>{i + 1}</span>
            <button type="button" data-testid={`track-play-${t.id}`} onClick={() => setPreview(preview === t.id ? null : t.id)} title="Dengarkan"
              style={{ width: 36, height: 36, borderRadius: 12, border: 0, background: preview === t.id ? D.blue : D.blueSoft, color: preview === t.id ? "#fff" : D.blue, display: "grid", placeItems: "center", cursor: "pointer" }}>
              <Music size={16} />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ display: "block", fontSize: 13.5, color: D.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</b>
              <span style={{ fontSize: 12, color: D.sub }}>{t.artist || "Tanpa artis"} · {fmtSize(t.size)}</span>
              {preview === t.id && <audio data-testid={`track-audio-${t.id}`} controls autoPlay src={api.mediaUrl(t.url)} style={{ display: "block", width: "100%", marginTop: 8, height: 34 }} />}
            </div>
            <Toggle on={!!t.active} testid={`track-active-${t.id}`} label="Aktif" onChange={v => api.patchTrack(token, t.id, { active: v }).then(reload).catch(e => setMsg(e.message))} />
            <div style={{ display: "flex", gap: 4 }}>
              <IconBtn testid={`track-up-${t.id}`} onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={14} /></IconBtn>
              <IconBtn testid={`track-down-${t.id}`} onClick={() => move(i, 1)} disabled={i === tracks.length - 1}><ArrowDown size={14} /></IconBtn>
              <IconBtn testid={`track-delete-${t.id}`} onClick={() => del(t)} danger><Trash2 size={14} /></IconBtn>
            </div>
          </li>
        ))}
      </ol>
      {tracks.length === 0 && <p data-testid="track-empty" style={{ color: D.sub, fontSize: 13, margin: "6px 0 0" }}>Playlist masih kosong — unggah lagu pertama.</p>}
    </Card>
  );
}

function Announcements({ token, items, reload, setMsg }) {
  const [form, setForm] = useState({ title: "", text: "" });
  const [edit, setEdit] = useState(null);
  const [proses, setProses] = useState("");
  async function buatAudio(a) {
    setProses(a.id); setMsg("");
    try { await api.buatAudioAnnounce(token, a.id); setMsg(`Audio "${a.title}" siap`); reload(); } catch (e) { setMsg(e.message); }
    setProses("");
  }

  async function submit(e) {
    e.preventDefault();
    setMsg("");
    try {
      if (edit) await api.patchAnnounce(token, edit, form); else await api.addAnnounce(token, { ...form, active: true });
      setForm({ title: "", text: "" }); setEdit(null); reload();
    } catch (err) { setMsg(err.message); }
  }
  async function del(a) {
    if (!window.confirm(`Hapus pengumuman "${a.title}"?`)) return;
    await api.deleteAnnounce(token, a.id).catch(e => setMsg(e.message));
    reload();
  }

  return (
    <Card testid="radio-announcements">
      <SectionTitle title="Pengumuman Suara AI" sub="Naskah dibacakan narator AI di sela lagu. Pilih sumber suara (Universal Key atau API key ElevenLabs sendiri), lalu klik 'Buat audio' per naskah." />
      <PengaturanNarator token={token} setMsg={setMsg} />
      <form onSubmit={submit} data-testid="announce-form" style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: D.cream, borderRadius: 18, marginBottom: 16 }}>
        <Field label="Judul"><input data-testid="announce-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="mis. Sambutan pagi" style={inputStyle} required /></Field>
        <Field label="Naskah"><textarea data-testid="announce-text" value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} placeholder="Tulis naskah yang akan dibacakan..." rows={4} style={{ ...inputStyle, resize: "vertical" }} required /></Field>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn type="submit" testid="announce-submit"><Megaphone size={15} /> {edit ? "Simpan Perubahan" : "Tambah Pengumuman"}</Btn>
          {edit && <Btn variant="ghost" testid="announce-cancel" onClick={() => { setEdit(null); setForm({ title: "", text: "" }); }}><X size={14} /> Batal</Btn>}
        </div>
      </form>
      <div data-testid="announce-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map(a => (
          <div key={a.id} data-testid={`announce-${a.id}`} style={{ padding: "12px 14px", borderRadius: 16, border: `1.5px solid ${D.line}`, opacity: a.active ? 1 : .55 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Mic size={15} color={D.blue} />
              <b style={{ flex: 1, fontSize: 13.5, color: D.ink }}>{a.title}</b>
              <Chip tone={a.audio_media_id ? "green" : "amber"}>{a.audio_media_id ? "Audio siap" : "Belum ada audio"}</Chip>
              {a.audio_media_id && <IconBtn testid={`announce-play-${a.id}`} title="Putar audio" onClick={() => new Audio(api.mediaUrl(a.audio_media_id)).play()}><Volume2 size={14} /></IconBtn>}
              <Btn variant="ghost" testid={`announce-audio-${a.id}`} onClick={() => buatAudio(a)} disabled={proses === a.id}><Mic size={13} /> {proses === a.id ? "Membuat..." : (a.audio_media_id ? "Buat ulang" : "Buat audio")}</Btn>
              <Toggle on={!!a.active} testid={`announce-active-${a.id}`} label="Aktif" onChange={v => api.patchAnnounce(token, a.id, { active: v }).then(reload).catch(e => setMsg(e.message))} />
              <IconBtn testid={`announce-edit-${a.id}`} onClick={() => { setEdit(a.id); setForm({ title: a.title, text: a.text }); }}><Pencil size={14} /></IconBtn>
              <IconBtn testid={`announce-delete-${a.id}`} onClick={() => del(a)} danger><Trash2 size={14} /></IconBtn>
            </div>
            <p style={{ margin: "8px 0 0", fontSize: 13, color: D.sub, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{a.text}</p>
          </div>
        ))}
        {items.length === 0 && <p data-testid="announce-empty" style={{ color: D.sub, fontSize: 13, margin: 0 }}>Belum ada naskah pengumuman.</p>}
      </div>
    </Card>
  );
}

export function IconBtn({ children, onClick, danger, disabled, testid, title }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} disabled={disabled} title={title}
      style={{ width: 30, height: 30, borderRadius: 10, border: `1.5px solid ${danger ? D.dangerSoft : D.line}`, background: "#fff", color: danger ? D.danger : D.ink, display: "grid", placeItems: "center", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? .4 : 1 }}>
      {children}
    </button>
  );
}
